import {
  executeSelectStage,
  buildLegacyMessages
} from './request';

const clipAuditTextLocal = (value = '', maxChars = 900) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  const limit = Math.max(120, Number(maxChars || 900));
  if (!text) return '';
  return text.length <= limit ? text : `${text.slice(0, Math.max(0, limit - 3)).trimEnd()}...`;
};

export const summarizeTypedPayloadForAudit = (typedPayload = null) => {
  if (!typedPayload || typeof typedPayload !== 'object') return null;

  return {
    task: {
      template: typedPayload?.task?.template || null,
      intent: typedPayload?.task?.intent || null
    },
    primaryInput: {
      source: typedPayload?.primary_input?.source || null,
      contentPreview: clipAuditTextLocal(typedPayload?.primary_input?.content || '')
    },
    instructionsPreview: clipAuditTextLocal(typedPayload?.instructions || '', 500),
    outputSpec: typedPayload?.output_spec || null,
    constraints: Array.isArray(typedPayload?.constraints)
      ? typedPayload.constraints.slice(0, 8).map((entry) => clipAuditTextLocal(entry, 220))
      : [],
    styleReference: typedPayload?.style_reference
      ? {
        source: typedPayload.style_reference.source || null,
        contentPreview: clipAuditTextLocal(typedPayload.style_reference.content || '', 420)
      }
      : null,
    retrievedContext: Array.isArray(typedPayload?.retrieved_context)
      ? typedPayload.retrieved_context.slice(0, 10).map((entry) => ({
        source: entry?.source || null,
        contentPreview: clipAuditTextLocal(entry?.content || '', 700)
      }))
      : [],
    instructionBindings: Array.isArray(typedPayload?.instruction_bindings)
      ? typedPayload.instruction_bindings.slice(0, 8)
      : [],
    conflictEvents: Array.isArray(typedPayload?._meta?.conflictEvents)
      ? typedPayload._meta.conflictEvents.slice(0, 8)
      : []
  };
};

export const summarizeTypedContextBlocks = (typedContext = null) => {
  if (!typedContext || typeof typedContext !== 'object') {
    return {
      requiredBlocks: [],
      optionalBlocks: [],
      optionalBlockCounts: {},
      conflictResolutionEvents: [],
      conflictResolutionReasonCodes: []
    };
  }

  const requiredBlocks = ['task', 'primary_input', 'instructions']
    .filter((key) => typedContext[key] !== undefined && typedContext[key] !== null);

  const optionalCandidates = [
    'style_reference',
    'retrieved_context',
    'constraints',
    'output_spec',
    'examples',
    'instruction_bindings'
  ];

  const optionalBlocks = optionalCandidates.filter((key) => {
    const value = typedContext[key];
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === 'object') return Object.keys(value).length > 0;
    return value !== undefined && value !== null && String(value).trim() !== '';
  });

  const optionalBlockCounts = {
    style_reference: typedContext?.style_reference ? 1 : 0,
    retrieved_context: Array.isArray(typedContext?.retrieved_context) ? typedContext.retrieved_context.length : 0,
    constraints: Array.isArray(typedContext?.constraints) ? typedContext.constraints.length : 0,
    output_spec: typedContext?.output_spec ? 1 : 0,
    examples: Array.isArray(typedContext?.examples) ? typedContext.examples.length : 0,
    instruction_bindings: Array.isArray(typedContext?.instruction_bindings)
      ? typedContext.instruction_bindings.length
      : 0
  };

  const conflictResolutionEvents = Array.isArray(typedContext?._meta?.conflictEvents)
    ? typedContext._meta.conflictEvents
    : [];
  const conflictResolutionReasonCodes = Array.from(new Set(
    conflictResolutionEvents
      .map((event) => String(event?.reasonCode || '').trim())
      .filter(Boolean)
  ));

  return {
    requiredBlocks,
    optionalBlocks,
    optionalBlockCounts,
    conflictResolutionEvents,
    conflictResolutionReasonCodes
  };
};

// Runs select-stage message construction with optional legacy fallback and emits stage telemetry.
export const executeSelectAndEmitStage = ({
  docJson,
  userText = '',
  systemPrompt,
  maxHistoryMessages = 20,
  runtimeEnabled = false,
  intentKey = null,
  chatMode = 'Explore',
  formatPolicy = null,
  contextItems = [],
  selectionContexts = [],
  contextChars = 0,
  contextReferences = [],
  usedFallbackRequest = null,
  usedLegacyRequest = false,
  fallbackEnabled = null,
  legacyFallbackEnabled = false,
  buildFallbackMessages,
  buildLegacyMessages: buildLegacyMessagesOverride,
  summarizeTypedPayload = summarizeTypedPayloadForAudit,
  summarizeTypedContext = summarizeTypedContextBlocks,
  goalState = null,
  emitStage,
  onTraceWarn
} = {}) => {
  const fallbackIsEnabled = fallbackEnabled === null
    ? legacyFallbackEnabled
    : Boolean(fallbackEnabled);
  const fallbackBuilderOverride = typeof buildFallbackMessages === 'function'
    ? buildFallbackMessages
    : buildLegacyMessagesOverride;
  let nextUsedLegacyRequest = Boolean(usedLegacyRequest);
  if (usedFallbackRequest !== null) {
    nextUsedLegacyRequest = Boolean(usedFallbackRequest);
  }
  let messages = [];
  let typedPayloadAudit = null;
  let typedContextSummary = {
    requiredBlocks: [],
    optionalBlocks: [],
    optionalBlockCounts: {},
    conflictResolutionEvents: [],
    conflictResolutionReasonCodes: []
  };

  try {
    const selectStageResult = executeSelectStage({
      docJson,
      userText,
      systemPrompt,
      maxHistoryMessages,
      orchestrationEnabled: Boolean(runtimeEnabled),
      intentKey: runtimeEnabled ? (intentKey || 'ask') : null,
      chatMode,
      formatPolicy: runtimeEnabled ? formatPolicy : null,
      contextItems: runtimeEnabled ? (Array.isArray(contextItems) ? contextItems : []) : [],
      selectionContexts: runtimeEnabled ? (Array.isArray(selectionContexts) ? selectionContexts : []) : [],
      goalState: runtimeEnabled ? goalState : null
    });

    messages = Array.isArray(selectStageResult?.messages) ? selectStageResult.messages : [];
    if (runtimeEnabled) {
      typedPayloadAudit = typeof summarizeTypedPayload === 'function'
        ? summarizeTypedPayload(selectStageResult?.typedPayload || null)
        : null;
      typedContextSummary = typeof summarizeTypedContext === 'function'
        ? summarizeTypedContext(selectStageResult?.typedPayload || null)
        : typedContextSummary;
    }
  } catch (error) {
    if (!(runtimeEnabled && fallbackIsEnabled)) {
      throw error;
    }
    nextUsedLegacyRequest = true;
    if (typeof onTraceWarn === 'function') {
      onTraceWarn({
        stage: 'select',
        fallback: 'legacy-messages',
        error: error?.message || 'orchestration message build failed'
      });
    }
    const legacyBuilder = typeof fallbackBuilderOverride === 'function'
      ? fallbackBuilderOverride
      : buildLegacyMessages;
    messages = typeof legacyBuilder === 'function'
      ? legacyBuilder({ docJson, userText, systemPrompt, maxHistoryMessages })
      : [];
  }

  if (typeof emitStage === 'function') {
    const consolidatedItems = (Array.isArray(contextItems) ? contextItems : [])
      .filter((item) => String(item?.source || '').trim() === 'consolidatedContext');
    const consolidatedContextChars = consolidatedItems.reduce((total, item) => {
      return total + String(item?.text || '').length;
    }, 0);
    const consolidatedSourceItemCount = consolidatedItems.reduce((total, item) => {
      return total + (Number(item?.meta?.sourceItemCount || 0) || 0);
    }, 0);

    emitStage('select', {
      messageCount: messages.length,
      contextCount: Array.isArray(contextItems) ? contextItems.length : 0,
      contextChars: Number(contextChars || 0),
      consolidatedContextItemCount: consolidatedItems.length,
      consolidatedContextChars,
      consolidatedSourceItemCount,
      contextReferences: Array.isArray(contextReferences) ? contextReferences : [],
      typedContextRequiredBlocks: Array.isArray(typedContextSummary.requiredBlocks) ? typedContextSummary.requiredBlocks : [],
      typedContextOptionalBlocks: Array.isArray(typedContextSummary.optionalBlocks) ? typedContextSummary.optionalBlocks : [],
      typedContextOptionalBlockCounts: typedContextSummary.optionalBlockCounts && typeof typedContextSummary.optionalBlockCounts === 'object'
        ? typedContextSummary.optionalBlockCounts
        : {},
      conflictResolutionCount: Array.isArray(typedContextSummary.conflictResolutionEvents)
        ? typedContextSummary.conflictResolutionEvents.length
        : 0,
      conflictResolutionReasonCodes: Array.isArray(typedContextSummary.conflictResolutionReasonCodes)
        ? typedContextSummary.conflictResolutionReasonCodes
        : [],
      conflictResolutionEvents: Array.isArray(typedContextSummary.conflictResolutionEvents)
        ? typedContextSummary.conflictResolutionEvents.slice(0, 5)
        : [],
      typedContextAudit: typedPayloadAudit,
      usedLegacyRequest: nextUsedLegacyRequest,
      usedFallbackRequest: nextUsedLegacyRequest
    });
  }

  return {
    messages,
    typedPayloadAudit,
    typedContextSummary,
    usedLegacyRequest: nextUsedLegacyRequest,
    usedFallbackRequest: nextUsedLegacyRequest
  };
};

export {
  executeSelectStage,
  buildLegacyMessages
};
